type WallClockParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

const DATE_TIME_INPUT =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

function formatter(timeZone: string): Intl.DateTimeFormat | null {
  try {
    return new Intl.DateTimeFormat("en-US-u-ca-gregory-nu-latn", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
  } catch {
    return null;
  }
}

function parseDateTimeInput(value: string): WallClockParts | null {
  const match = DATE_TIME_INPUT.exec(value);
  if (!match) return null;

  const parts = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
    second: Number(match[6] ?? 0),
  };
  const date = new Date(0);
  date.setUTCFullYear(parts.year, parts.month - 1, parts.day);
  date.setUTCHours(parts.hour, parts.minute, parts.second, 0);

  if (
    date.getUTCFullYear() !== parts.year ||
    date.getUTCMonth() !== parts.month - 1 ||
    date.getUTCDate() !== parts.day ||
    date.getUTCHours() !== parts.hour ||
    date.getUTCMinutes() !== parts.minute ||
    date.getUTCSeconds() !== parts.second
  ) {
    return null;
  }

  return parts;
}

function wallClockPartsAt(
  instantMs: number,
  dateTimeFormatter: Intl.DateTimeFormat,
): WallClockParts {
  const values = dateTimeFormatter
    .formatToParts(new Date(instantMs))
    .reduce<Record<string, string>>((result, part) => {
      if (part.type !== "literal") result[part.type] = part.value;
      return result;
    }, {});

  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    hour: Number(values.hour) === 24 ? 0 : Number(values.hour),
    minute: Number(values.minute),
    second: Number(values.second),
  };
}

function partsToUtcMs(parts: WallClockParts): number {
  const date = new Date(0);
  date.setUTCFullYear(parts.year, parts.month - 1, parts.day);
  date.setUTCHours(parts.hour, parts.minute, parts.second, 0);
  return date.getTime();
}

function sameWallClock(left: WallClockParts, right: WallClockParts): boolean {
  return (
    left.year === right.year &&
    left.month === right.month &&
    left.day === right.day &&
    left.hour === right.hour &&
    left.minute === right.minute &&
    left.second === right.second
  );
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

export function toZonedDateTimeInput(
  iso: string | null | undefined,
  timeZone: string | null | undefined,
): string {
  if (!iso || !timeZone) return "";
  const date = new Date(iso);
  const dateTimeFormatter = formatter(timeZone);
  if (Number.isNaN(date.getTime()) || !dateTimeFormatter) return "";

  const parts = wallClockPartsAt(date.getTime(), dateTimeFormatter);
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`;
}

export function toZonedCivilDate(instant: Date, timeZone: string): Date {
  const dateTimeFormatter = formatter(timeZone);
  if (Number.isNaN(instant.getTime()) || !dateTimeFormatter) {
    throw new RangeError("A valid instant and facility timezone are required.");
  }

  const parts = wallClockPartsAt(instant.getTime(), dateTimeFormatter);
  return new Date(parts.year, parts.month - 1, parts.day);
}

export function createZonedTimeFormatter(timeZone: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
  });
}

export function fromZonedDateTimeInput(
  value: string,
  timeZone: string | null | undefined,
): string | null {
  const target = parseDateTimeInput(value);
  if (!target || !timeZone) return null;
  const dateTimeFormatter = formatter(timeZone);
  if (!dateTimeFormatter) return null;

  const targetUtcMs = partsToUtcMs(target);
  const offsets = new Set<number>();
  const sampleStepMs = 6 * 60 * 60 * 1000;
  for (
    let delta = -36 * 60 * 60 * 1000;
    delta <= 36 * 60 * 60 * 1000;
    delta += sampleStepMs
  ) {
    const sampleMs = targetUtcMs + delta;
    offsets.add(
      partsToUtcMs(wallClockPartsAt(sampleMs, dateTimeFormatter)) - sampleMs,
    );
  }

  const matches = Array.from(offsets)
    .map((offset) => targetUtcMs - offset)
    .filter((candidateMs) =>
      sameWallClock(target, wallClockPartsAt(candidateMs, dateTimeFormatter)),
    )
    .sort((left, right) => left - right);

  return matches.length > 0 ? new Date(matches[0]).toISOString() : null;
}
